import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSound } from '../hooks/useSound';
import { Sidebar } from '../components/Sidebar';
import { StatusPill } from '../components/StatusPill';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ScanBarcode, 
  CheckCircle2, 
  Loader2,
  AlertTriangle,
  Lock,
  Package,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function Conference() {
  const location = useLocation();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { playSuccess, playError, playExcess, playNotFound } = useSound();
  
  const [invoice, setInvoice] = useState(location.state?.invoice || null);
  const [conference, setConference] = useState(null);
  const [items, setItems] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showGestorModal, setShowGestorModal] = useState(false);
  const [finalizePassword, setFinalizePassword] = useState('');
  const [gestorPassword, setGestorPassword] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [gestorQuantity, setGestorQuantity] = useState('');
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null);
  const [bottomNotice, setBottomNotice] = useState(null);
  
  const inputRef = useRef(null);
  const noticeTimeoutRef = useRef(null);

  useEffect(() => {
    if (invoice && !conference) {
      startConference();
    }
  }, [invoice]);

  useEffect(() => {
    // Keep focus on scan input
    if (inputRef.current && !showFinalizeModal && !showGestorModal) {
      inputRef.current.focus();
    }
  }, [items, showFinalizeModal, showGestorModal]);

  useEffect(() => {
    return () => {
      if (noticeTimeoutRef.current) {
        clearTimeout(noticeTimeoutRef.current);
      }
    };
  }, []);

  const showBottomNotice = (type, message) => {
    if (noticeTimeoutRef.current) {
      clearTimeout(noticeTimeoutRef.current);
    }

    setBottomNotice({ type, message });
    noticeTimeoutRef.current = setTimeout(() => {
      setBottomNotice(null);
      noticeTimeoutRef.current = null;
    }, 3500);
  };

  const startConference = async () => {
    setLoading(true);
    try {
      const response = await api.post('/conferences/start', { invoice_id: invoice.id });
      setConference(response.data);
      setItems(response.data.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar conferência');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scanInput.trim() || !conference) return;

    const ean = scanInput.trim();
    setScanInput('');
    setLastScanResult(null);

    try {
      const response = await api.post('/conferences/check-item', {
        conference_id: conference.id,
        ean: ean,
        quantity: 1
      });

      if (response.data.success) {
        playSuccess();
        setLastScanResult({ type: 'success', item: response.data.item });
        
        // Update local items - busca por EAN ou código interno (ignorando zeros à esquerda)
        const searchCode = ean.replace(/^0+/, '') || ean;
        setItems(prev => prev.map(item => {
          const itemEan = (item.ean || '').replace(/^0+/, '') || item.ean;
          const itemInternal = (item.internal_code || '').replace(/^0+/, '') || item.internal_code;
          if (itemEan === searchCode || itemInternal === searchCode || item.ean === ean || item.internal_code === ean) {
            return { ...item, ...response.data.item };
          }
          return item;
        }));
        
        const remaining = response.data.item.remaining_quantity;
        if (remaining > 0) {
          showBottomNotice(
            'success',
            `${response.data.item.description} - Conferido: ${response.data.item.checked_quantity} | Faltam: ${remaining}`
          );
        } else {
          showBottomNotice('success', `${response.data.item.description} - COMPLETO! ✓`);
        }
      } else {
        if (response.data.error === 'EXCESS_QUANTITY') {
          playExcess();
          setLastScanResult({ type: 'excess', message: response.data.message });
          showBottomNotice('error', response.data.message);
        } else if (response.data.error === 'PRODUCT_NOT_FOUND') {
          playNotFound();
          setLastScanResult({ type: 'notFound', message: response.data.message });
          showBottomNotice('warning', 'Produto não encontrado na NF-e');
        }
      }
    } catch (error) {
      playError();
      showBottomNotice('error', 'Erro ao processar item');
    }
  };

  const handleGestorUnlock = async () => {
    if (!selectedItem || !gestorPassword || selectedItemIndex === null) return;

    // Usa EAN se existir, senão usa código interno
    const itemCode = selectedItem.ean || selectedItem.internal_code;
    
    try {
      await api.post('/conferences/gestor-unlock', {
        conference_id: conference.id,
        password: gestorPassword,
        ean: itemCode,
        new_quantity: parseFloat(gestorQuantity)
      });

      // Update local items - usa o índice para garantir atualização correta
      const newQuantity = parseFloat(gestorQuantity);
      setItems(prev => prev.map((item, index) => {
        if (index === selectedItemIndex) {
          return { 
            ...item, 
            checked_quantity: newQuantity,
            status: newQuantity === item.expected_quantity ? 'completed' : newQuantity > 0 ? 'partial' : 'pending'
          };
        }
        return item;
      }));

      toast.success('Quantidade atualizada pelo gestor');
      setShowGestorModal(false);
      setGestorPassword('');
      setGestorQuantity('');
      setSelectedItem(null);
      setSelectedItemIndex(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desbloquear');
    }
  };

  const handleCancelConference = async () => {
    if (window.confirm('Tem certeza que deseja cancelar esta conferência? O progresso será perdido.')) {
      try {
        // Cancela a conferência no backend
        await api.post('/conferences/cancel', {
          conference_id: conference.id
        });
        toast.success('Conferência cancelada');
        navigate('/dashboard');
      } catch (error) {
        // Se não existir o endpoint de cancelar, apenas volta pro dashboard
        navigate('/dashboard');
      }
    }
  };

  const handleFinalize = async () => {
    if (!finalizePassword) return;

    try {
      await api.post('/conferences/finalize', {
        conference_id: conference.id,
        password: finalizePassword
      });

      setShowFinalizeModal(false);
      setFinalizePassword('');
      setShowSuccessScreen(true);
      playSuccess();
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar');
    }
  };

  const canFinalize = () => {
    return items.filter(i => i.is_checkable).every(i => i.status === 'completed');
  };

  const getProgress = () => {
    const checkable = items.filter(i => i.is_checkable);
    const completed = checkable.filter(i => i.status === 'completed').length;
    return checkable.length > 0 ? Math.round((completed / checkable.length) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </main>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
          <div className="max-w-2xl mx-auto text-center py-20">
            <Package size={64} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-2xl font-heading font-bold text-slate-700 mb-2">
              Nenhuma NF-e Selecionada
            </h2>
            <p className="text-slate-500 mb-6">
              Selecione uma NF-e no dashboard ou faça upload de um XML para iniciar a conferência.
            </p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
            >
              Ir para Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Success Screen
  if (showSuccessScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-emerald-500 conference-success">
        <div className="text-center text-white animate-fade-in">
          <CheckCircle2 size={120} className="mx-auto mb-6" />
          <h1 className="text-5xl font-heading font-bold mb-4">
            Conferência Finalizada com Sucesso!
          </h1>
          <p className="text-2xl opacity-90">
            NF-e {invoice.invoice_number} travada permanentemente
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-6 pb-28 md:p-8 md:pb-32">
        {/* Header */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#1a365d]" data-testid="conference-title">
              Conferência NF-e {invoice.invoice_number}
            </h1>
            <p className="text-slate-500 font-mono text-sm mt-1">
              Chave: {invoice.invoice_key}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-slate-500">Progresso</p>
              <p className="text-2xl font-heading font-bold text-[#1a365d]">
                {getProgress()}%
              </p>
            </div>
            
            <Button
              onClick={handleCancelConference}
              variant="outline"
              className="h-12 px-6 border-red-300 text-red-600 hover:bg-red-50"
              data-testid="cancel-conference-button"
            >
              <XCircle className="mr-2" size={18} />
              Cancelar
            </Button>
            
            <Button
              onClick={() => setShowFinalizeModal(true)}
              disabled={!canFinalize()}
              className={`h-12 px-6 ${
                canFinalize() 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'bg-slate-300 cursor-not-allowed'
              }`}
              data-testid="finalize-button"
            >
              <Lock className="mr-2" size={18} />
              Finalizar Conferência
            </Button>
          </div>
        </div>

        {/* Scanner Input */}
        <div className={`bg-white rounded-xl border-2 p-6 mb-6 transition-all ${
          lastScanResult?.type === 'success' ? 'border-emerald-500 animate-pulse-success' :
          lastScanResult?.type === 'excess' ? 'border-red-500 animate-shake' :
          lastScanResult?.type === 'notFound' ? 'border-amber-500 animate-shake' :
          'border-slate-200'
        }`}>
          <form onSubmit={handleScan}>
            <Label className="text-lg font-heading font-semibold text-slate-700 mb-3 block">
              <ScanBarcode className="inline mr-2" size={20} />
              Bipar Código EAN ou Código Interno
            </Label>
            <Input
              ref={inputRef}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Aguardando leitura do código de barras ou código do produto..."
              className="h-16 text-3xl font-mono text-center border-2 border-slate-300 focus:ring-4 focus:ring-[#1a365d]/20 focus:border-[#1a365d]"
              autoFocus
              data-testid="scan-input"
            />
          </form>
          
          {lastScanResult && (
            <div className={`mt-4 p-3 rounded-lg text-center ${
              lastScanResult.type === 'success' ? 'bg-emerald-100 text-emerald-800' :
              lastScanResult.type === 'excess' ? 'bg-red-100 text-red-800' :
              'bg-amber-100 text-amber-800'
            }`}>
              {lastScanResult.type === 'success' && (
                <div>
                  <p className="font-medium text-lg">
                    {lastScanResult.item.description}
                  </p>
                  <p className="text-sm mt-1">
                    Conferido: <span className="font-bold">{lastScanResult.item.checked_quantity}</span> / 
                    Esperado: <span className="font-bold">{lastScanResult.item.expected_quantity}</span>
                    {lastScanResult.item.remaining_quantity > 0 ? (
                      <span className="ml-2 text-amber-700 font-bold">| Faltam: {lastScanResult.item.remaining_quantity}</span>
                    ) : (
                      <span className="ml-2 text-emerald-700 font-bold">| COMPLETO ✓</span>
                    )}
                  </p>
                </div>
              )}
              {lastScanResult.type !== 'success' && (
                <p className="font-medium flex items-center justify-center gap-2">
                  <AlertTriangle size={18} />
                  {lastScanResult.message}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold text-slate-800">
              Itens da NF-e ({items.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">EAN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Esperado</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Conferido</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Faltam</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item, index) => (
                  <tr 
                    key={index}
                    className={`transition-colors ${
                      item.status === 'completed' ? 'bg-emerald-50/50' :
                      item.status === 'partial' ? 'bg-amber-50/50' :
                      !item.is_checkable ? 'bg-slate-50' :
                      'bg-red-50/30'
                    }`}
                    data-testid={`item-row-${item.ean || index}`}
                  >
                    <td className="px-4 py-3 font-mono text-sm text-slate-700">
                      {item.ean || '-'}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-600">
                      {item.internal_code}
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[250px] truncate">
                      {item.description}
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-medium text-slate-800">
                      {item.expected_quantity}
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-bold text-lg">
                      <span className={
                        item.status === 'completed' ? 'text-emerald-600' :
                        item.status === 'partial' ? 'text-amber-600' :
                        'text-slate-400'
                      }>
                        {item.checked_quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-bold text-lg">
                      <span className={
                        item.expected_quantity - item.checked_quantity === 0 ? 'text-emerald-600' :
                        item.expected_quantity - item.checked_quantity < item.expected_quantity ? 'text-amber-600' :
                        'text-red-600'
                      }>
                        {item.expected_quantity - item.checked_quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusPill status={item.is_checkable ? item.status : 'not_checkable'} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.is_checkable && item.status !== 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedItem(item);
                            setSelectedItemIndex(index);
                            setGestorQuantity(item.checked_quantity.toString());
                            setShowGestorModal(true);
                          }}
                          data-testid={`edit-btn-${item.ean || index}`}
                        >
                          Editar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Finalize Modal */}
        <Dialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Finalizar Conferência</DialogTitle>
              <DialogDescription>
                Digite sua senha para confirmar a finalização. A NF-e será travada permanentemente.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label htmlFor="finalize-password">Sua senha</Label>
              <Input
                id="finalize-password"
                type="password"
                value={finalizePassword}
                onChange={(e) => setFinalizePassword(e.target.value)}
                placeholder="Digite sua senha"
                className="mt-2"
                data-testid="finalize-password-input"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFinalizeModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleFinalize}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-finalize-button"
              >
                Confirmar Finalização
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gestor Unlock Modal */}
        <Dialog open={showGestorModal} onOpenChange={setShowGestorModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Desbloqueio de Gestor</DialogTitle>
              <DialogDescription>
                {selectedItem?.description}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div>
                <Label>Quantidade (máx: {selectedItem?.expected_quantity})</Label>
                <Input
                  type="number"
                  value={gestorQuantity}
                  onChange={(e) => setGestorQuantity(e.target.value)}
                  max={selectedItem?.expected_quantity}
                  min={0}
                  className="mt-2"
                  data-testid="gestor-quantity-input"
                />
              </div>
              
              <div>
                <Label>Senha do Gestor</Label>
                <Input
                  type="password"
                  value={gestorPassword}
                  onChange={(e) => setGestorPassword(e.target.value)}
                  placeholder="Digite a senha do gestor"
                  className="mt-2"
                  data-testid="gestor-password-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGestorModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGestorUnlock}
                className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                data-testid="confirm-gestor-button"
              >
                Confirmar Alteração
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      {bottomNotice && (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
          <div
            className={`max-w-3xl rounded-xl border px-4 py-3 text-sm font-medium shadow-lg ${
              bottomNotice.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : bottomNotice.type === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {bottomNotice.message}
          </div>
        </div>
      )}
    </div>
  );
}
